import { FC } from 'react';
import classNames from 'classnames';
import { sort } from 'fast-sort';
import useSWR from 'swr';

import { Slideout, SlideoutCommonProps } from 'components/design-system/Slideout';
import { useBezelUser } from 'context/bezelUser';
import { clientApiGet } from 'lib/api_helper';
import styles from 'styles/Auctions.module.scss';
import { Bid, Listing, ProxyBid } from 'types';
import { getFormattedDate } from 'utils/text-helpers';

import { AllBidsLoader } from './AllBidsLoader';
import { BidItem } from './BidItem';

import { useOnPlaceBid } from '../hooks';

type AllBidsModalProps = {
  listing: Listing;
  bidValue?: number;
  proxyBid?: ProxyBid;
} & SlideoutCommonProps;

export const AllBidsModal: FC<AllBidsModalProps> = ({ listing, closeModal, isOpen, bidValue, proxyBid }) => {
  const { user } = useBezelUser();

  const { data: bids, isLoading: loadingBids } = useSWR<Bid[]>(`/api/auctions/${listing.id}/bids`, clientApiGet);

  const isMyOwnListing = user?.sellerProfile && user?.sellerProfile?.id === listing.sellerProfile.id;

  const onPlaceBid = useOnPlaceBid(listing, bidValue, proxyBid);

  const endsOn = getFormattedDate(listing.auctionInfo.endDate);
  const isEnded = listing.auctionInfo.ended;
  const orderedBids = sort(bids || []).desc('id');
  const ownHighestBid = user && orderedBids.find(bid => bid.buyerProfile?.id === user?.buyerProfile?.id);
  const maxBid = proxyBid && proxyBid.listing.id === listing.id ? proxyBid : null;
  const onPlaceBidClick = () => {
    closeModal?.();
    onPlaceBid();
  };

  const userHasBeenOutbid = ownHighestBid && ownHighestBid.priceCents < listing.auctionInfo.highestBid?.priceCents;
  const buttonText = ownHighestBid ? 'Increase your bid' : 'Place a bid';
  let buttonVariant;

  if (userHasBeenOutbid) {
    buttonVariant = 'pink';
  } else {
    buttonVariant = 'turquoise';
  }

  return (
    <Slideout
      isTitleFixed
      isDark
      isOpen={isOpen}
      closeModal={closeModal}
      className={styles.modal}
      bodyClassName={classNames('px-0 py-0', { 'overflow-hidden': loadingBids })}
      title={
        <>
          <div className='mb-2'>Bids</div>
          {!isEnded && listing.auctionInfo.endDate && (
            <div className='fs-12px'>
              <span className='opacity-60'>ENDS ON </span>
              <span>{endsOn}</span>
            </div>
          )}
        </>
      }
      submit={
        isEnded || isMyOwnListing
          ? undefined
          : {
              title: buttonText,
              onClick: onPlaceBidClick,
              variant: buttonVariant
            }
      }
    >
      <div className='fs-12px pb-5 riforma-regular text-center text-light d-flex flex-column justify-content-around align-items-center'>
        <div className='w-100 position-relative'>
          <div className='d-flex text-light w-100 py-3 px-2 sticky-top' style={{ backgroundColor: '#242526', top: -8 }}>
            <div className='w-10 opacity-60'>#</div>
            <div className='w-20 text-start opacity-60'>BIDDER</div>
            <div className='w-25 text-start opacity-60'>BID AMOUNT</div>
            <div className='w-45 opacity-60'>TIME PLACED</div>
          </div>
          {loadingBids && <AllBidsLoader />}
          {!loadingBids && bids && bids.length === 0 && (
            <div className='text-light text-center my-3'>
              No bids yet. {!isMyOwnListing && 'Be the first to bid on this item!'}
            </div>
          )}
          {!loadingBids && bids && bids.length > 0 && (
            <>
              <div className={styles.allBidsContent}>
                {orderedBids.map((bid, index) => (
                  <BidItem
                    key={`bid-${bid.bidderId}-${index}`}
                    bidAmountCents={bid.priceCents}
                    bidder={'Bidder ' + bid.bidderId.toString()}
                    timePlaced={getFormattedDate(bid.created)}
                    bidNumber={bids?.length - index}
                    isOwn={user && bid.buyerProfile?.id === user?.buyerProfile?.id}
                    rowNumber={index}
                    maxBidCents={ownHighestBid?.id === bid.id && maxBid ? maxBid.maxPriceCents : null}
                    auctionLive={listing.auctionInfo.live}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </Slideout>
  );
};
